/**
 * File Name          : air-racer.component.ts
 * Author             : Jared Laine Dyreson
 * Version            : 0.1.0
 * Date               : 2026-03-18
 * Description        : Short Description (manual edit required)
 *
 * Copyright (C) Innovative Design Labs, Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * This file is subject to the terms and conditions defined in
 * file 'IDL_PROJECTTECH_LICENSE.txt', which is part of this source code package.
 */

import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild, ChangeDetectorRef } from '@angular/core';
import { AirRacerResults } from '../../../lesson-utils/air_racer-analysis';
import { CommonModule } from '@angular/common';
import { DxButtonGroupModule, DxButtonModule, DxChartModule, DxPopupComponent, DxChartComponent } from 'devextreme-angular';
import { AirRacerFields } from '../../../models/lessons.model';
import { variableSigFigsFormatter } from '../../../utils/formatters';
import { ScreenserviceService } from '../../../services/screenservice.service';
import { Subscription } from 'rxjs';

const AVAILABLE_SERIES = [{text: 'Acceleration (G)', valueField: 'acceleration', type:'default'}, {text: 'Velocity (m/s)', valueField: 'velocity', type:'default'}, {text: 'Distance (m)', valueField: 'position', type:'default'}]


@Component({
  selector: 'app-air-racer',
  standalone: true,
  imports: [CommonModule, DxChartModule, DxButtonGroupModule, DxButtonModule, DxPopupComponent],
  templateUrl: './air-racer.component.html',
  styleUrl: './air-racer.component.scss'
})
export class AirRacerComponent implements OnChanges, OnInit, OnDestroy {
  @Input() results: AirRacerResults | undefined = undefined;
  @Input() scalars: string[] = [];
  @Input() series: string[] = [];

  @ViewChild(DxChartComponent) chart?: DxChartComponent;

  mobileChartSelection: string|null = null;
  chartKey: number = 0; // Used to force chart re-render on orientation change

  private orientationSub?: Subscription;

  AirRacerFields = AirRacerFields;

  constructor(
    public screenservice: ScreenserviceService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    // Driven by the service's settled orientation signal rather than a raw
    // window resize/orientationchange listener - see ScreenserviceService for why.
    this.orientationSub = this.screenservice.orientationChanges.subscribe(() => this.onOrientationChange());
  }

  ngOnDestroy(): void {
    this.orientationSub?.unsubscribe();
  }

  onOrientationChange(): void {
    // Force chart re-render on orientation change
    this.chartKey++;
    this.cdr.detectChanges();

    // Give the DOM time to update, then force a full remeasure - without
    // { force: true } DevExtreme can skip re-measuring the container after
    // rotation, leaving the chart sized to a stale (pre-rotation) reading,
    // which is what produced the cramped/truncated layout on rotation.
    setTimeout(() => {
      if (this.chart?.instance) {
        this.chart.instance.render({ force: true });
      }
    }, 100);
  }

  // The mobile chart popup can be opened without any intervening rotation
  // event (device already in its current orientation) - the service's own
  // listeners never fire in that case, so isLandscape could still reflect a
  // stale read from before the page settled. Force a fresh check right as
  // the popup opens instead of relying solely on passive orientation events.
  openMobileChart(seriesValueField: string): void {
    this.screenservice.recheckOrientation();
    this.mobileChartSelection = seriesValueField;
    this.selectedDataSeries = [seriesValueField];
    this.onOrientationChange();
  }

  // DevExtreme mounts the popup's content on its own show/animation lifecycle,
  // not synchronously with the click that sets mobileChartSelection - relying
  // solely on that click-time check left a window where the popup could still
  // render against a stale orientation read. onShowing re-validates right
  // before content mounts; onShown re-measures once the popup has finished
  // animating in and the chart has its final on-screen size.
  onPopupShowing(): void {
    this.screenservice.recheckOrientation();
  }

  onPopupShown(): void {
    this.onOrientationChange();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['scalars']) {
      // console.log('scalars changed:', changes['scalars'].currentValue);
    }
    if (changes['series']) {
      // console.log('series changed:', changes['series'].currentValue);
      this.availableDataSeries = AVAILABLE_SERIES.filter((s: any) => (changes['series'].currentValue as string[]).includes(s.valueField))
      this.selectedDataSeries = []
    }
  }


  localVariableSigFigsFormatter = variableSigFigsFormatter;

  availableDataSeries = AVAILABLE_SERIES as any;
  selectedDataSeries: string[] = [];


  isSeriesSelected(series: string): boolean {
    return this.selectedDataSeries.includes(series);
  }
  customizeTooltip = ({ argumentText, valueText }: Record<string, string>) => {
    return {
      text: `${Number(argumentText).toFixed(2)}s, ${Number(valueText).toFixed(2)}${this.getUnits()}`,
    };
  }

  getUnits = () => {
    if(this.isSeriesSelected('acceleration')) return "G"
    if(this.isSeriesSelected('velocity')) return "m/s"
    if(this.isSeriesSelected('position')) return "m"
    return ""
  }

  roundDownChartLabel = (argument: { value: string | number | Date; valueText: string; }) => {
    const value = argument.value as number;
    return (value >= 0 ? Math.floor(value) : Math.ceil(value)).toString();
  }

  getChartPopupTitle = () => {
    return AVAILABLE_SERIES.find(s => s.valueField === this.mobileChartSelection)?.text ?? "Data Chart";
  }

  // The Launch zone should span the full recorded data, not just the
  // post-trigger-detection portion - launch_start marks where the analysis
  // detected the launch (trigger threshold crossing), leaving the actual
  // start of the recording (time 0) uncolored. The Launch->Race boundary
  // (launch_end) is unaffected.
  getLaunchStripStart = (): number | undefined => {
    return this.results?.data?.[0]?.timestamp ?? this.results?.timeline?.launch_start;
  }

  // Zone labels are rendered as HTML spans overlaid on the chart plot area
  // rather than as DevExtreme strip labels. DX centers a strip label on its
  // midpoint and clips at the plot edges, which drops the label for narrow
  // zones pinned to the domain edge (Launch, at t=0). We render our own
  // spans and position them from the argument axis's visual range and pixel
  // extent, correcting for the SVG's DOM offset so overlay coordinates line
  // up with the plot. Wired to both onDrawn and onZoomEnd so it runs on
  // initial render, every DX redraw, and every zoom/pan gesture.
  //
  // Not using the SVG strip <rect>'s getBoundingClientRect for positioning:
  // DX draws each strip as a wide rect and clips it visually with a
  // clip-path, so the rect's own bounds extend well past the plot area and
  // are useless as a positioning source.
  onChartDrawn = (e: any) => {
    // Defer to the next frame so DX has finished writing axis state for
    // the current zoom/pan gesture before we read visualRange/getVisibleArea.
    requestAnimationFrame(() => this.repositionZoneLabels(e));
  }

  // On zoom, DX recomputes the value axis's left margin from the new tick
  // labels' text metrics, but that first pass sometimes uses stale sizing
  // and under-allocates space for the rotated axis title, clipping it
  // against the container's left edge. A forced second render (same pattern
  // used for orientation change above) makes DX re-measure and re-layout
  // against the current tick labels, which corrects the margin.
  onChartZoomEnd = (e: any) => {
    requestAnimationFrame(() => this.repositionZoneLabels(e));
    setTimeout(() => {
      e.component?.render?.({ force: true });
    }, 50);
  }

  private repositionZoneLabels(e: any): void {
    const timeline = this.results?.timeline;
    if (!timeline) return;

    const chartEl: HTMLElement | undefined = e.element instanceof HTMLElement
      ? e.element
      : (typeof e.component?.element === 'function' ? e.component.element() : undefined);
    if (!chartEl) return;

    const wrapper = chartEl.closest('.chart-with-labels') as HTMLElement | null;
    if (!wrapper) return;
    const overlay = wrapper.querySelector<HTMLElement>(':scope > .zone-label-overlay');
    if (!overlay) return;

    const svg = chartEl.querySelector('svg');
    if (!svg) {
      this.hideAllZoneLabels(overlay);
      return;
    }

    const argAxis = e.component.getArgumentAxis?.();
    if (!argAxis) {
      this.hideAllZoneLabels(overlay);
      return;
    }

    const vr = argAxis.visualRange?.();
    if (!vr || vr.startValue == null || vr.endValue == null || vr.startValue >= vr.endValue) {
      this.hideAllZoneLabels(overlay);
      return;
    }

    // Axis pixel extent in the SVG's own coordinate system. In DX 25 this
    // is [startPx, endPx]; guard for the object shape too.
    const visibleArea = argAxis.getVisibleArea?.();
    let axisStart: number | undefined;
    let axisEnd: number | undefined;
    if (Array.isArray(visibleArea) && visibleArea.length >= 2) {
      axisStart = visibleArea[0];
      axisEnd = visibleArea[1];
    } else if (visibleArea && typeof visibleArea === 'object') {
      axisStart = (visibleArea as any).start;
      axisEnd = (visibleArea as any).end;
    }
    if (axisStart == null || axisEnd == null || axisEnd <= axisStart) {
      this.hideAllZoneLabels(overlay);
      return;
    }

    // Translate SVG-space axis pixels into overlay-space pixels. The overlay
    // covers the wrapper with inset:0, so overlay origin == wrapper origin.
    const svgRect = svg.getBoundingClientRect();
    const wrapperRect = wrapper.getBoundingClientRect();
    const svgOffsetX = svgRect.left - wrapperRect.left;
    const plotLeft = svgOffsetX + axisStart;
    const plotRight = svgOffsetX + axisEnd;
    // Zone labels sit just below the plot's actual top edge, not the
    // overlay's - the overlay spans the whole chart component, but DX
    // reserves space above the plot rectangle (e.g. for chart padding),
    // so a fixed CSS `top` sits above the strip rects instead of on them.
    const svgOffsetY = svgRect.top - wrapperRect.top;
    const labelTop = svgOffsetY + 6;
    if (plotRight <= plotLeft) {
      this.hideAllZoneLabels(overlay);
      return;
    }

    const dataToPx = (v: number) =>
      plotLeft + ((v - vr.startValue) / (vr.endValue - vr.startValue)) * (plotRight - plotLeft);

    const zones: { key: string; start: number | undefined; end: number | undefined }[] = [
      { key: 'launch', start: this.getLaunchStripStart(), end: timeline.launch_end },
      { key: 'race',   start: timeline.launch_end,   end: timeline.impact_start },
      { key: 'impact', start: timeline.impact_start, end: timeline.impact_end },
    ];

    type Placement = { el: HTMLElement; cx: number; halfW: number; visWidthPx: number; hide: boolean };
    const placements: (Placement | null)[] = zones.map(z => {
      const el = overlay.querySelector<HTMLElement>(`[data-zone="${z.key}"]`);
      if (!el) return null;
      el.style.visibility = 'hidden';

      if (z.start == null || z.end == null || z.start >= z.end) return null;

      const visStart = Math.max(z.start, vr.startValue);
      const visEnd = Math.min(z.end, vr.endValue);
      if (visStart >= visEnd) return null;

      const leftPx = dataToPx(visStart);
      const rightPx = dataToPx(visEnd);
      const visWidthPx = rightPx - leftPx;
      if (visWidthPx <= 0) return null;

      const labelW = el.offsetWidth;
      if (labelW <= 0) return null;

      // Clamp to the plot area so the label always sits over the plot,
      // not over the y-axis labels or the right-margin whitespace.
      const margin = 2;
      let cx = leftPx + visWidthPx / 2;
      cx = Math.max(plotLeft + labelW / 2 + margin, cx);
      cx = Math.min(plotRight - labelW / 2 - margin, cx);

      return { el, cx, halfW: labelW / 2, visWidthPx, hide: false };
    });

    // Overlap resolution: order by rendered x-center; if adjacent bboxes
    // touch, hide the label whose zone has the smaller visible slice.
    const visible = placements.filter((p): p is Placement => p !== null);
    visible.sort((a, b) => a.cx - b.cx);
    const collisionPad = 4;
    for (let i = 0; i < visible.length - 1; i++) {
      const a = visible[i];
      const b = visible[i + 1];
      if (a.hide || b.hide) continue;
      if (a.cx + a.halfW + collisionPad > b.cx - b.halfW) {
        (a.visWidthPx < b.visWidthPx ? a : b).hide = true;
      }
    }

    for (const p of placements) {
      if (!p) continue;
      if (p.hide) {
        p.el.style.visibility = 'hidden';
      } else {
        p.el.style.left = `${p.cx}px`;
        p.el.style.top = `${labelTop}px`;
        p.el.style.visibility = 'visible';
      }
    }
  }

  private hideAllZoneLabels(overlay: HTMLElement): void {
    overlay.querySelectorAll<HTMLElement>('.zone-label').forEach(el => {
      el.style.visibility = 'hidden';
    });
  }
}
